import React, { useRef, useEffect, useState, useMemo } from 'react';
import { getMediaUrl } from '../services/api';
import { getRandomSilhouette } from '../utils/salsaSilhouettes';
import './VideoThumbnail.css';

interface VideoThumbnailProps {
  videoUrl: string;
  thumbnailUrl?: string;
  alt: string;
  onClick: () => void;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ videoUrl, thumbnailUrl, alt, onClick }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const fallbackSilhouette = useMemo(() => getRandomSilhouette(), []);

  const fullVideoUrl = videoUrl
    ? (videoUrl.startsWith('http') ? videoUrl : getMediaUrl(videoUrl))
    : '';

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      // Seek to 2 seconds to get a good frame
      video.currentTime = 2;
    };

    const handleSeeked = () => {
      setIsLoaded(true);
    };

    const handleError = () => {
      setHasError(true);
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('error', handleError);

    // Fallback timeout for iOS/Safari where video preload may not work
    const timeout = setTimeout(() => {
      if (!isLoaded) setHasError(true);
    }, 5000);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
      clearTimeout(timeout);
    };
  }, [isLoaded]);

  // If we have a thumbnail URL, try to use it first
  if (thumbnailUrl && !hasError) {
    return (
      <button type="button" className="video-thumbnail-container" onClick={onClick} aria-label={`Play ${alt}`}>
        <img
          src={getMediaUrl(thumbnailUrl)}
          alt=""
          className="thumbnail-image"
          onError={() => setHasError(true)}
        />
        <div className="play-overlay" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </button>
    );
  }

  // No video URL — show random silhouette without play overlay
  if (!fullVideoUrl) {
    return (
      <div className="video-thumbnail-container">
        <img
          src={fallbackSilhouette}
          alt={alt}
          className="thumbnail-image"
        />
      </div>
    );
  }

  return (
    <button type="button" className="video-thumbnail-container" onClick={onClick} aria-label={`Play ${alt}`}>
      {/* Video element to extract frame */}
      <video
        ref={videoRef}
        src={fullVideoUrl}
        className={`thumbnail-video ${isLoaded ? 'loaded' : ''}`}
        muted
        playsInline
        preload="auto"
        crossOrigin="anonymous"
      />

      {/* Loading placeholder */}
      {!isLoaded && !hasError && (
        <div className="thumbnail-loading">
          <img
            src={fallbackSilhouette}
            alt=""
            className="thumbnail-loading-silhouette"
          />
        </div>
      )}

      {/* Error fallback */}
      {hasError && (
        <img
          src={fallbackSilhouette}
          alt=""
          className="thumbnail-image"
        />
      )}

      {/* Play overlay */}
      <div className="play-overlay" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
    </button>
  );
};

export default VideoThumbnail;
